import {
  Box,
  Divider,
  GKButton,
  GKCheckbox,
  GKSearchSelect,
  GKTextField,
  Grid2,
  IconButton,
  InfolconIcon,
  Show,
  SimpleLoader,
  Tooltip,
  Typography,
} from "components";
import GKImageUploader from "components/GKImageUploader";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { isEmpty } from "lodash";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  SELECTED_CITY,
  SELECTED_COUNTRY,
  SELECTED_STATE,
} from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { handleFreshReturnFormData } from "utils";
import { GET_COMPANY_BY_ID } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { AccountType } from "utils/common";
import { BANK_ACCOUNT_TYPES } from "utils/constants";
import { QueryKeys } from "utils/queryKey";

const Billing = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const userInfo = useAppSelector((state) => state?.user?.userData);
  const companyID = userInfo?.companyId;

  const userData = useAppSelector((state) => state?.user?.userData);
  const countryList = useAppSelector((state) => state?.app?.countryList);
  const stateList = useAppSelector((state) => state?.app?.stateList);
  const cityList = useAppSelector((state) => state?.app?.cityList);

  const dispatch = useAppDispatch();

  const { data, isFetching, refetch } = useFetchQuery({
    key: ["GET_BILLING_DETAILS"],
    route: `/billing-details/`,
    success: (response: any) => {
      setCroppedImage(response?.data?.qrCode);
    },
  });

  const { data: companyData } = useFetchQuery({
    key: [QueryKeys.COMPANY_INFO, companyID],
    route: GET_COMPANY_BY_ID(companyID),
    enabled: Boolean(companyID),
  });

  const { mutate } = useMutation({
    mutationKey: ["BILLING"],
    mutationFn: (data: any) =>
      axiosInstance.patch(`/billing-details/${userData?.id}/`, data),
    onSuccess: () => {
      refetch();
    },
  });

  const form = useForm({
    defaultValues: {
      pan: "",
      gst: "",
      cinCode: "",
      sacCode: "",
      address: "",
      pincode: "",
      country: "",
      state: "",
      city: "",
      accountName: "",
      accountNumber: "",
      accountType: "",
      bankName: "",
      ifscCode: "",
      qrCode: null,
      isSamePermanentAddress: "",
    },
    values: data?.data?.data,
  });

  if (isFetching) return <SimpleLoader />;

  const handlePhotoDelete = (field: string) => {
    const formData = new FormData();
    formData.append(field, "");
    mutate(formData);
  };

  const onSubmit = (values: any) => {
    let newObject = { ...values };

    if (typeof newObject.isSamePermanentAddress === "boolean") {
      newObject.isSamePermanentAddress = newObject.isSamePermanentAddress
        ? "true"
        : "false";
    }

    if (isEmpty(newObject?.qrCode) || typeof newObject?.qrCode === "string") {
      delete newObject.qrCode;
    } else {
      newObject = { ...newObject, qrCode: values.qrCode[0] };
    }

    mutate(
      handleFreshReturnFormData({
        isEditForm: false,
        obj: {
          ...newObject,
        },
      }),
    );
  };

  form.watch();

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} id="billing-form">
      <Grid2 container spacing={2}>
        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Invoicing Information
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={6}>
            <Controller
              name="pan"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please Enter PAN",
                },
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKTextField
                    {...field}
                    inputLabel="PAN"
                    onChange={(e) =>
                      field.onChange(e.target.value.toUpperCase())
                    }
                    placeholder="Eg: ABSDF1230J"
                    requiredField
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="gst"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="GSTN"
                  placeholder="GSTN"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="cinCode"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="CIN Code"
                  placeholder="CIN Code"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="sacCode"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="SAC Code"
                  placeholder="SAC Code"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Correspondence Address
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12} display={"flex"} alignItems={"center"}>
            <Controller
              name="isSamePermanentAddress"
              control={form.control}
              render={({ field }) => (
                <GKCheckbox
                  checked={!!field.value}
                  onChange={(e) => {
                    const { checked } = e.target;
                    field.onChange(checked);
                    if (checked) {
                      form.setValue("address", companyData?.data.address);
                      form.setValue("pincode", companyData?.data.pincode);
                      form.setValue("state", companyData?.data.state);
                      form.setValue("country", companyData?.data.country);
                      form.setValue("city", companyData?.data.city);
                    } else {
                      form.setValue("address", "");
                      form.setValue("pincode", "");
                      form.setValue("state", "");
                      form.setValue("country", "");
                      form.setValue("city", "");
                    }
                  }}
                />
              )}
            />

            <Typography
              style={{
                fontSize: 14,
                fontWeight: 400,
                color: "var(--neutral-500)",
              }}
            >
              Same as Billing Address
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="address"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please Enter Address",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Address"
                  requiredField
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={3}>
            <Controller
              name="country"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please Select Country",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="Country"
                  error={!!error}
                  helperText={error?.message}
                  requiredField
                  options={countryList || []}
                  getOptionLabel={(e) => e.name || e}
                  onChange={(e, val) => {
                    field.onChange(val.name);
                    form.setValue("city", "");
                    form.setValue("state", "");
                    dispatch(SELECTED_COUNTRY(val.id));
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={3}>
            <Controller
              name="state"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please Select State",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="State"
                  error={!!error}
                  helperText={error?.message}
                  requiredField
                  options={stateList || []}
                  getOptionLabel={(e) => e.name || e}
                  onChange={(e, val) => {
                    field.onChange(val.name);
                    form.setValue("city", "");
                    dispatch(SELECTED_STATE(val.id));
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={3}>
            <Controller
              name="city"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please Select City",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="City"
                  error={!!error}
                  helperText={error?.message}
                  requiredField
                  options={cityList || []}
                  getOptionLabel={(e) => e.name || e}
                  onChange={(e, val) => {
                    field.onChange(val.name);
                    dispatch(SELECTED_CITY(val.id));
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={3}>
            <Controller
              name="pincode"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please Enter Pincode",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  type="number"
                  inputLabel="Pincode"
                  requiredField
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Bank Details
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={4}>
            <Controller
              name="accountName"
              control={form.control}
              render={({ field }) => (
                <GKTextField {...field} inputLabel="Account Name" />
              )}
            />
          </Grid2>

          <Grid2 size={4}>
            <Controller
              name="accountNumber"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  type="number"
                  inputLabel="Account Number"
                />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="accountType"
              control={form.control}
              render={({ field }) => (
                <GKSearchSelect
                  {...field}
                  disableClearable={false}
                  inputLabel="Account Type"
                  options={BANK_ACCOUNT_TYPES || []}
                  onChange={(e, val) => {
                    field.onChange(val?.value);
                  }}
                  value={
                    BANK_ACCOUNT_TYPES?.find(
                      (option: any) => field.value === option.value,
                    )?.name || []
                  }
                  getOptionLabel={(option) => option.name || option}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="bankName"
              control={form.control}
              render={({ field }) => (
                <GKTextField {...field} inputLabel="Bank Name" />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="ifscCode"
              control={form.control}
              render={({ field }) => (
                <GKTextField {...field} inputLabel="IFSC Code" />
              )}
            />
          </Grid2>
        </Grid2>

        <Show.When isTrue={userInfo?.accountType !== AccountType.RA}>
          <Grid2 size={12} my={1}>
            <Divider />
          </Grid2>

          <Grid2 size={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                fontSize={16}
                fontWeight={500}
                color={"var(--neutral-500)"}
              >
                Upload QR Code
              </Typography>
              <IconButton>
                <Tooltip
                  arrow
                  title="Add a scannable QR code to collect invoice payments."
                  placement="right"
                >
                  <InfolconIcon width={16} height={16} />
                </Tooltip>
              </IconButton>
            </Box>
          </Grid2>

          <Grid2 container size={9} spacing={2}>
            <Grid2 container size={12} gap={1}>
              <div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Grid2 size={12}>
                    <Controller
                      name="qrCode"
                      control={form.control}
                      render={({ field }) => (
                        <GKImageUploader
                          croppedImage={croppedImage || field.value}
                          setCroppedImage={setCroppedImage}
                          handleProfilePicDelete={() =>
                            handlePhotoDelete("qrCode")
                          }
                          selectedImage={selectedImage}
                          setSelectedImage={setSelectedImage}
                          form={form}
                          logoName="qrCode"
                          buttonName="Upload QR Code"
                        />
                      )}
                    />
                  </Grid2>
                </div>

                <Grid2 mt={2} ml={1.5}>
                  <Typography color="#9AA4B2">
                    Format should be JPG or PNG, 3:4 aspect ratio, up to 10MB
                  </Typography>
                </Grid2>
              </div>
            </Grid2>
          </Grid2>
        </Show.When>

        <Grid2 size={12} textAlign={"right"} my={1}>
          <GKButton form="billing-form" variant="contained" type="submit">
            Save
          </GKButton>
        </Grid2>
      </Grid2>
    </form>
  );
};

export default Billing;
